import React, { useRef, useCallback, useEffect } from "react";
import {
  angleToPosition,
  angleToValue,
  positionToAngle,
  valueToAngle,
  arcPathWithRoundedEnds,
} from "./circularMath";
import styles from "./CircularSlider.module.css";

const TRACK_WIDTH = 4;
const SHADOW_WIDTH = 20;

/**
 * @typedef {{ direction: 'cw' | 'ccw', axis: '+x' | '-x' | '+y' | '-y' }} AngleDescription
 *
 * @typedef {Object} HandleProps
 * @property {number} value
 * @property {(value: number) => void} [onChange]
 * @property {string} [color]
 * @property {boolean} [showValue]
 */

/**
 * @param {Object} props
 * @param {number} [props.size=200]
 * @param {number} [props.minValue=0]
 * @param {number} [props.maxValue=100]
 * @param {number} [props.startAngle=0]
 * @param {number} [props.endAngle=360]
 * @param {AngleDescription} [props.angleType]
 * @param {number} [props.handleSize=8]
 * @param {HandleProps} props.handle1
 * @param {HandleProps} [props.handle2]
 * @param {() => void} [props.onControlFinished]
 * @param {boolean} [props.disabled=false]
 * @param {string} props.arcColor
 * @param {string} [props.arcBackgroundColor='#aaa']
 * @param {boolean} [props.coerceToInt=false]
 * @param {boolean} [props.outerShadow=false]
 */
export const CircularSlider = ({
  size = 200,
  minValue = 0,
  maxValue = 100,
  startAngle = 0,
  endAngle = 360,
  angleType = { direction: "cw", axis: "-y" },
  handleSize = 8,
  handle1,
  handle2,
  onControlFinished,
  disabled = false,
  arcColor,
  arcBackgroundColor = "#aaa",
  coerceToInt = false,
  outerShadow = false,
}) => {
  const svgRef = useRef(null);

  // Keep mutable props in a ref so stable callbacks always read the latest values
  const liveProps = useRef({});
  liveProps.current = {
    handle1,
    handle2,
    size,
    minValue,
    maxValue,
    angleType,
    startAngle,
    endAngle,
    coerceToInt,
    disabled,
  };

  const onControlFinishedRef = useRef(onControlFinished);
  useEffect(() => {
    onControlFinishedRef.current = onControlFinished;
  });

  const processSelection = useCallback((x, y) => {
    const {
      handle1,
      handle2,
      size,
      minValue,
      maxValue,
      angleType,
      startAngle,
      endAngle,
      coerceToInt,
      disabled,
    } = liveProps.current;
    if (!handle1.onChange) {
      return;
    }
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const pt = svg.createSVGPoint();
    pt.x = x;
    pt.y = y;
    const coords = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    const angle = positionToAngle(coords, size, angleType);
    let value = angleToValue({
      angle,
      minValue,
      maxValue,
      startAngle,
      endAngle,
    });
    if (coerceToInt) value = Math.round(value);

    if (!disabled) {
      if (
        handle2?.onChange &&
        Math.abs(value - handle2.value) < Math.abs(value - handle1.value)
      ) {
        handle2.onChange(value);
      } else {
        handle1.onChange(value);
      }
    }
  }, []);

  const onMouseMove = useCallback(
    (ev) => processSelection(ev.clientX, ev.clientY),
    [processSelection],
  );

  // stableRemove must be stable so addEventListener/removeEventListener see the same reference
  const removeRef = useRef(null);
  const stableRemove = useCallback(() => removeRef.current?.(), []);
  removeRef.current = () => {
    const svg = svgRef.current;
    if (svg) {
      svg.removeEventListener("mousemove", onMouseMove);
      svg.removeEventListener("mouseleave", stableRemove);
      svg.removeEventListener("mouseup", stableRemove);
    }
    onControlFinishedRef.current?.();
  };

  const onMouseDown = useCallback(
    (ev) => {
      if (ev.target.localName !== "circle" && ev.target.localName !== "text") {
        ev.stopPropagation();
        return;
      }
      const svg = svgRef.current;
      if (svg) {
        svg.addEventListener("mousemove", onMouseMove);
        svg.addEventListener("mouseleave", stableRemove);
        svg.addEventListener("mouseup", stableRemove);
      }
      onMouseMove(ev);
    },
    [onMouseMove, stableRemove],
  );

  const onMouseEnter = useCallback(
    (ev) => {
      if (ev.buttons === 1) {
        onMouseDown(ev);
      }
    },
    [onMouseDown],
  );

  const onTouch = useCallback(
    (ev) => {
      if (ev.type !== "touchmove") {
        return;
      }
      if (ev.target.localName !== "circle" && ev.target.localName !== "text") {
        return;
      }
      if (
        ev.touches.length > 1 ||
        (ev.type === "touchend" && ev.touches.length > 0)
      ) {
        return;
      }
      const touch = ev.changedTouches[0];
      processSelection(touch.clientX, touch.clientY);
    },
    [processSelection],
  );

  if (disabled) {
    return null;
  }

  const trackInnerRadius = size / 2 - TRACK_WIDTH - SHADOW_WIDTH;
  const arcCenter = trackInnerRadius + TRACK_WIDTH / 2;

  const h1Angle = valueToAngle({
    value: handle1.value,
    minValue,
    maxValue,
    startAngle,
    endAngle,
  });
  const h2Angle =
    handle2 != null
      ? valueToAngle({
          value: handle2.value,
          minValue,
          maxValue,
          startAngle,
          endAngle,
        })
      : null;

  const h1Pos = angleToPosition(
    { degree: h1Angle, ...angleType },
    arcCenter,
    size,
  );
  const h2Pos =
    h2Angle != null
      ? angleToPosition({ degree: h2Angle, ...angleType }, arcCenter, size)
      : null;

  const controllable = Boolean(handle1.onChange);

  const arcOpts = (sa, ea) => ({
    startAngle: sa,
    endAngle: ea,
    angleType,
    innerRadius: trackInnerRadius,
    thickness: TRACK_WIDTH,
    svgSize: size,
    direction: angleType.direction,
  });

  return (
    <svg
      width={size}
      height={size}
      ref={svgRef}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onClick={(ev) => controllable && ev.stopPropagation()}
      onTouchStart={onTouch}
      onTouchEnd={onTouch}
      onTouchMove={onTouch}
      className={styles.svg}
    >
      {outerShadow && (
        <>
          <radialGradient id="outerShadow">
            <stop offset="90%" stopColor={arcColor} />
            <stop offset="100%" stopColor="white" />
          </radialGradient>
          <circle
            fill="none"
            stroke="url(#outerShadow)"
            cx={size / 2}
            cy={size / 2}
            r={trackInnerRadius + TRACK_WIDTH + SHADOW_WIDTH / 2 - 1}
            strokeWidth={SHADOW_WIDTH}
          />
        </>
      )}

      {h2Angle == null ? (
        <>
          <path
            d={arcPathWithRoundedEnds(arcOpts(h1Angle, endAngle))}
            fill={arcBackgroundColor}
          />
          <path
            d={arcPathWithRoundedEnds(arcOpts(startAngle, h1Angle))}
            fill={arcColor}
          />
        </>
      ) : (
        <>
          <path
            d={arcPathWithRoundedEnds(arcOpts(startAngle, h1Angle))}
            fill={arcBackgroundColor}
          />
          <path
            d={arcPathWithRoundedEnds(arcOpts(h2Angle, endAngle))}
            fill={arcBackgroundColor}
          />
          <path
            d={arcPathWithRoundedEnds(arcOpts(h1Angle, h2Angle))}
            fill={arcColor}
          />
        </>
      )}

      {controllable && (
        <>
          <filter id="handleShadow" x="-50%" y="-50%" width="16" height="16">
            <feOffset result="offOut" in="SourceGraphic" dx="0" dy="0" />
            <feColorMatrix
              result="matrixOut"
              in="offOut"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
            />
            <feGaussianBlur result="blurOut" in="matrixOut" stdDeviation="5" />
            <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
          </filter>
          <circle
            r={handleSize}
            cx={h1Pos.x}
            cy={h1Pos.y}
            fill={handle1.color || "#fff"}
            filter="url(#handleShadow)"
            className={styles.handle}
          />
          {handle1.showValue && (
            <text
              x={h1Pos.x}
              y={h1Pos.y}
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="14px"
              className={styles.handle}
            >
              {handle1.value}
              <tspan fontSize="14px" dy={-1} dx={1}>
                °
              </tspan>
            </text>
          )}
        </>
      )}

      {h2Pos && (
        <>
          <circle
            r={handleSize}
            cx={h2Pos.x}
            cy={h2Pos.y}
            fill={handle2.color || "#fff"}
            filter="url(#handleShadow)"
            className={styles.handle}
          />
          {handle2.showValue && (
            <text
              x={h2Pos.x}
              y={h2Pos.y}
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="14px"
              className={styles.handle}
            >
              {handle2.value}
              <tspan fontSize="14px" dy={-1} dx={1}>
                °
              </tspan>
            </text>
          )}
        </>
      )}
    </svg>
  );
};

/**
 * @param {import('react').PropsWithChildren<Parameters<typeof CircularSlider>[0]>} props
 */
export const CircularSliderWithChildren = ({
  size = 200,
  children,
  ...props
}) => (
  <div
    className={styles.withChildrenWrapper}
    style={{ width: size, height: size }}
  >
    <CircularSlider size={size} {...props} />
    <div className={styles.withChildrenContent}>{children}</div>
  </div>
);

export default CircularSlider;
